// frontend/src/services/dashboardService.ts
import { api } from './api';
import type {
  DashboardMetrics,
  RecentSale,
  SalesChartData,
  TopProduct,
  LowStockProduct
} from '../types';

/**
 * Servicio para interactuar con el API del Dashboard
 */
class DashboardService {
  /**
   * Obtiene las métricas principales del dashboard
   */
  async getMetrics(): Promise<DashboardMetrics> {
    try {
      const response = await api.get('/dashboard/metrics');
      return response.data.data;
    } catch (error) {
      console.error('Error obteniendo métricas:', error);
      throw error;
    }
  }

  /**
   * Obtiene las ventas recientes
   * @param limit - Número de ventas a obtener (default: 10)
   */
  async getRecentSales(limit: number = 10): Promise<RecentSale[]> {
    try {
      const response = await api.get('/dashboard/recent-sales', {
        params: { limit }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error obteniendo ventas recientes:', error);
      throw error;
    }
  }

  /**
   * Obtiene datos para el gráfico de ventas
   * @param period - Periodo de tiempo: 'week' | 'month' | 'year'
   */
  async getSalesChartData(period: 'week' | 'month' | 'year' = 'month'): Promise<SalesChartData[]> {
    try {
      const response = await api.get('/dashboard/sales-chart', {
        params: { period }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error obteniendo datos del gráfico:', error);
      throw error;
    }
  }

  /**
   * Obtiene los productos más vendidos
   * @param limit - Número de productos a obtener (default: 5)
   */
  async getTopProducts(limit: number = 5): Promise<TopProduct[]> {
    try {
      const response = await api.get('/dashboard/top-products', {
        params: { limit }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error obteniendo productos top:', error);
      throw error;
    }
  }

  /**
   * Obtiene productos con stock bajo
   * @param limit - Número de productos a obtener (default: 10)
   */
  async getLowStockProducts(limit: number = 10): Promise<LowStockProduct[]> {
    try {
      const response = await api.get('/dashboard/low-stock', {
        params: { limit }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error obteniendo productos con stock bajo:', error);
      throw error;
    }
  }

  /**
   * Obtiene el resumen del dashboard consolidado (Métricas + Ventas Recientes + Comparación)
   * Útil para reducir el número de peticiones en producción
   */
  async getSummary(limit: number = 5): Promise<{
    metrics: DashboardMetrics;
    recentSales: RecentSale[];
    comparison: any;
  }> {
    try {
      const response = await api.get('/dashboard/summary', { params: { limit } });
      return response.data.data;
    } catch (error) {
      console.error('Error obteniendo resumen del dashboard:', error);
      throw error;
    }
  }

  /**
   * Obtiene las métricas operativas estilo Excel (diario y mensual por método de pago)
   * @param month - Mes (0-11)
   * @param year - Año
   */
  async getOperationalMetricsExcel(month: number, year: number): Promise<{
    storeNames: string[];
    dailyMetrics: Array<{ period: string; total: number; stores: Record<string, { efectivo: number; tarjeta: number; transferencia: number; mercado_pago: number; kueski: number; total: number }> }>;
    monthlyMetrics: Array<{ period: string; total: number; stores: Record<string, { efectivo: number; tarjeta: number; transferencia: number; mercado_pago: number; kueski: number; total: number }> }>;
    month: number;
    year: number;
  }> {
    try {
      const response = await api.get('/dashboard/operational-metrics-excel', {
        params: { month, year }
      });
      return response.data.data;
    } catch (error) {
      console.error('Error obteniendo métricas:', error);
      throw error;
    }
  }
}

// Exportar instancia singleton
export const dashboardService = new DashboardService();
export default dashboardService;
